import type { StatusColorGroup } from '../statusDefinitions';

/**
 * Alert Banner (piece 3): a page-level message in one of the four status tones
 * with an optional dismiss and an optional action link. A banner that states a
 * problem and offers no way to it is a decoration, so the two tones that state
 * problems require an action.
 *
 * 🔒 A fifth tone, Info, is the page speaking rather than something being wrong
 * (decision 1218): navy on light blue, stating no problem, so it needs no action.
 * It is a banner tone alone and never a status colour group.
 */
export type BannerTone = StatusColorGroup | 'info';

export interface BannerAction {
  label: string;
  onAction: () => void;
}

export function statesProblem(tone: BannerTone): boolean {
  return tone === 'warning' || tone === 'error';
}

/**
 * 🔒 A Warning banner may be the page urging rather than stating a fault (decision
 * 1485): the Deal's Urgent next action names what to do and draws no action link,
 * so the caller says it is urging and the Warning tone needs none. The Error tone
 * is never exempt.
 */
export function assertBannerActionable(tone: BannerTone, action: BannerAction | undefined, urging = false): void {
  if (urging && tone === 'warning') return;
  if (statesProblem(tone) && !action) {
    throw new Error('An Alert Banner that states a problem offers an action (piece 3)');
  }
}
